import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ArrowDownCircle, ArrowUpCircle, Percent, Info, Pencil, Check, X, RotateCcw, Loader2, RefreshCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact three-tile KPI card showing the business's average payment cycles
 * and gross margin, computed from the last ~365 days of Xero data.
 *
 * Each tile supports inline edit (pencil → input → Save/Cancel). Overrides
 * are persisted via POST /api/dashboard/payment-stats/overrides and an
 * "Edited" badge appears with a reset button so the user can fall back to
 * the Xero-computed value at any time.
 */
export default function PaymentStatsCard({ stats, onUpdated }) {
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  if (!stats) return null;

  const recomputeGrossMargin = async () => {
    setRecomputing(true);
    try {
      const r = await axios.post(`${API.replace(/\/api$/, '')}/api/xero/recompute-gross-margin`);
      const data = r.data || {};
      if (data.computed) {
        toast.success(`Gross margin updated: ${data.gross_margin_percent}%`);
      } else {
        // Build a friendly diagnostic message
        const seen = (data.scanned_titles || []).map((t) => t.title).slice(0, 6).join(', ');
        toast.error(
          data.note
            || `Could not find Gross Profit / Total Income in your Xero P&L (saw: ${seen || 'no rows'}). Enter the value manually using the pencil icon.`,
          { duration: 8000 }
        );
      }
      onUpdated && (await onUpdated());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to recompute. Make sure Xero is connected.');
    } finally {
      setRecomputing(false);
    }
  };

  const formatUpdated = (iso) => {
    if (!iso) return null;
    try {
      return format(parseISO(iso), 'dd MMM yyyy');
    } catch (_) {
      return null;
    }
  };

  const startEdit = (key, currentValue) => {
    setEditingKey(key);
    setDraft(currentValue !== null && currentValue !== undefined ? String(currentValue) : '');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft('');
  };

  const saveEdit = async (key) => {
    const num = parseFloat(draft);
    if (Number.isNaN(num)) {
      toast.error('Please enter a valid number');
      return;
    }
    if ((key === 'avg_days_to_receive' || key === 'avg_days_to_pay_bills') && (num < 0 || num > 3650)) {
      toast.error('Days must be between 0 and 3650');
      return;
    }
    if (key === 'gross_margin_percent' && (num < -100 || num > 100)) {
      toast.error('Gross margin must be between -100 and 100');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/dashboard/payment-stats/overrides`, { [key]: num });
      toast.success('Saved');
      setEditingKey(null);
      setDraft('');
      onUpdated && (await onUpdated());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (key) => {
    setSaving(true);
    try {
      const flag = {
        avg_days_to_receive: 'clear_avg_days_to_receive',
        avg_days_to_pay_bills: 'clear_avg_days_to_pay_bills',
        gross_margin_percent: 'clear_gross_margin_percent',
      }[key];
      await axios.post(`${API}/dashboard/payment-stats/overrides`, { [flag]: true });
      toast.success('Reverted to Xero value');
      onUpdated && (await onUpdated());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const renderTile = ({
    key, fieldKey, icon: Icon, label, suffix,
    computed, override, effective,
    sample, sampleLabel, accentClass, ringClass, updatedAt, helper,
    extraAction = null,
  }) => {
    const isEditing = editingKey === fieldKey;
    const hasOverride = override !== null && override !== undefined;
    return (
      <div
        className={`relative flex items-start gap-3 rounded-lg border ${ringClass} p-3.5 bg-white dark:bg-slate-900 transition-shadow hover:shadow-sm`}
        data-testid={`payment-stat-${key}`}
      >
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${accentClass}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {label}
            </p>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-slate-400 hover:text-slate-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{helper}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasOverride && !isEditing && (
              <span
                className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[9px] font-bold uppercase tracking-wider"
                title={`Manually edited. Xero value: ${computed ?? '—'}`}
                data-testid={`payment-stat-${key}-edited-badge`}
              >
                Edited
              </span>
            )}
          </div>

          {isEditing ? (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Input
                type="number"
                step={fieldKey === 'gross_margin_percent' ? '0.1' : '1'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit(fieldKey);
                  if (e.key === 'Escape') cancelEdit();
                }}
                className="h-8 w-24 font-mono"
                autoFocus
                data-testid={`payment-stat-${key}-input`}
              />
              <span className="text-xs text-slate-500">{suffix}</span>
              <Button
                size="sm"
                onClick={() => saveEdit(fieldKey)}
                disabled={saving}
                className="h-8 w-8 p-0"
                title="Save"
                data-testid={`payment-stat-${key}-save`}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEdit}
                disabled={saving}
                className="h-8 w-8 p-0"
                title="Cancel"
                data-testid={`payment-stat-${key}-cancel`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : effective === null || effective === undefined ? (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p className="text-sm text-slate-400" data-testid={`payment-stat-${key}-empty`}>
                Insufficient data
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => startEdit(fieldKey, '')}
                className="h-7 px-1.5 text-slate-400 hover:text-primary"
                title="Set manually"
                data-testid={`payment-stat-${key}-edit`}
              >
                <Pencil className="w-3 h-3 mr-1" /> Set
              </Button>
              {extraAction}
            </div>
          ) : (
            <div className="flex items-baseline gap-1.5 mt-0.5 flex-wrap">
              <p
                className="text-2xl font-bold font-mono text-slate-900 dark:text-slate-100 tabular-nums"
                data-testid={`payment-stat-${key}-value`}
              >
                {fieldKey === 'gross_margin_percent' ? Number(effective).toFixed(1) : Math.round(effective)}
              </p>
              <span className="text-xs text-slate-500">{suffix}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => startEdit(fieldKey, effective)}
                className="h-7 w-7 p-0 ml-1 text-slate-400 hover:text-primary"
                title="Edit"
                data-testid={`payment-stat-${key}-edit`}
              >
                <Pencil className="w-3 h-3" />
              </Button>
              {hasOverride && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => clearOverride(fieldKey)}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-primary"
                  title={`Reset to Xero value${computed != null ? ` (${computed})` : ''}`}
                  data-testid={`payment-stat-${key}-reset`}
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
              {extraAction}
            </div>
          )}

          {!isEditing && (sample > 0 || updatedAt) && (
            <p className="text-[10px] text-slate-400 mt-0.5">
              {sample > 0 && (
                <span>{sample} {sampleLabel}</span>
              )}
              {sample > 0 && updatedAt && <span> · </span>}
              {updatedAt && <span>Synced {formatUpdated(updatedAt)}</span>}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-slate-200/80 shadow-sm" data-testid="payment-stats-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
            Payment Cycle &amp; Margin
          </p>
          <p className="text-[10px] text-slate-400">
            Based on the last {stats.period_days || 365} days of Xero data
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {renderTile({
            key: 'dso',
            fieldKey: 'avg_days_to_receive',
            icon: ArrowDownCircle,
            label: 'Avg days to receive payment',
            suffix: 'days',
            computed: stats.avg_days_to_receive_computed,
            override: stats.avg_days_to_receive_override,
            effective: stats.avg_days_to_receive_effective,
            sample: stats.ar_sample_size || 0,
            sampleLabel: stats.ar_sample_size === 1 ? 'paid invoice' : 'paid invoices',
            accentClass: 'bg-emerald-500',
            ringClass: 'border-emerald-200/60 hover:border-emerald-300',
            updatedAt: stats.ar_updated_at,
            helper: 'Average days from invoice date to fully-paid date, across paid invoices in the last 365 days. Click the pencil to override the Xero value with your own figure.',
          })}
          {renderTile({
            key: 'dpo',
            fieldKey: 'avg_days_to_pay_bills',
            icon: ArrowUpCircle,
            label: 'Avg days to pay bills',
            suffix: 'days',
            computed: stats.avg_days_to_pay_bills_computed,
            override: stats.avg_days_to_pay_bills_override,
            effective: stats.avg_days_to_pay_bills_effective,
            sample: stats.ap_sample_size || 0,
            sampleLabel: stats.ap_sample_size === 1 ? 'paid bill' : 'paid bills',
            accentClass: 'bg-blue-600',
            ringClass: 'border-blue-200/60 hover:border-blue-300',
            updatedAt: stats.ap_updated_at,
            helper: 'Average days from bill date to fully-paid date, across paid bills in the last 365 days. Click the pencil to override the Xero value with your own figure.',
          })}
          {renderTile({
            key: 'gm',
            fieldKey: 'gross_margin_percent',
            icon: Percent,
            label: 'Avg gross margin',
            suffix: '%',
            computed: stats.gross_margin_percent_computed,
            override: stats.gross_margin_percent_override,
            effective: stats.gross_margin_percent_effective,
            sample: 0,
            sampleLabel: '',
            accentClass: 'bg-violet-600',
            ringClass: 'border-violet-200/60 hover:border-violet-300',
            updatedAt: stats.gross_margin_updated_at,
            helper: (() => {
              const ml = stats.gross_margin_matched_labels || {};
                  const inc = ml.total_income;
                  const cogs = ml.total_cost_of_sales;
                  const base = 'Gross Margin % = 100 − (Total COGS ÷ Total Revenue × 100), from the last 365 days of your Xero Profit & Loss report.';
                  if (inc || cogs) {
                    return `${base} Matched: Revenue → "${inc || '?'}", COGS → "${cogs || '?'}". Click the pencil to override or the refresh icon to recompute from Xero.`;
                  }
                  return `${base} Click the pencil to override with your own figure, or the refresh icon to recompute from Xero now.`;
            })(),
            extraAction: (
              <Button
                size="sm"
                variant="ghost"
                onClick={recomputeGrossMargin}
                disabled={recomputing}
                className="h-7 w-7 p-0 text-slate-400 hover:text-violet-600"
                title="Recompute from Xero P&L now"
                data-testid="payment-stat-gm-recompute"
              >
                {recomputing
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <RefreshCw className="w-3 h-3" />}
              </Button>
            ),
          })}
        </div>
      </CardContent>
    </Card>
  );
}
